<template>
  <div class="inline-flex">
    <button
      ref="trigger"
      @click="toggle"
      :class="[
        'p-2 rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed',
        open ? 'text-gray-700 bg-gray-100' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
      ]"
      :title="title"
      :disabled="!visibleItems.length"
    >
      <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.75"/><circle cx="12" cy="12" r="1.75"/><circle cx="12" cy="19" r="1.75"/></svg>
    </button>

    <!-- Teleported so the menu isn't clipped by scrollable containers
         (e.g. .table-scroll). Positioned against the trigger's rect. -->
    <Teleport to="body">
      <div
        v-if="open"
        ref="menu"
        class="fixed z-[600] min-w-[180px] py-1 bg-white rounded-xl border border-gray-200 shadow-lg animate-fade-in"
        :style="menuStyle"
      >
        <template v-for="(item, i) in visibleItems" :key="item.key || item.label || i">
          <div v-if="item.divider" class="my-1 border-t border-gray-100" />
          <component
            v-else
            :is="item.to ? 'router-link' : 'button'"
            :to="item.to"
            @click="select(item)"
            :class="[
              'w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-left transition-colors cursor-pointer',
              item.danger ? 'text-red-500 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'
            ]"
          >
            <svg
              v-if="item.icon"
              class="w-4 h-4 shrink-0"
              :class="item.danger ? 'text-red-400' : 'text-gray-400'"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
              v-html="item.icon"
            />
            <span class="truncate">{{ item.label }}</span>
          </component>
        </template>
      </div>
    </Teleport>
  </div>
</template>

<script>
const MENU_GAP = 4

/**
 * Kebab (vertical three dots) actions dropdown.
 *
 * items: [{
 *   label:   String,
 *   icon:    String  — inner SVG markup (24x24 viewBox, stroke icons),
 *   to:      Object|String — router-link target (navigates),
 *   action:  Function — called on click (when no `to`),
 *   danger:  Boolean — red styling (e.g. Delete),
 *   show:    Boolean — defaults to true; false hides the item,
 *   divider: Boolean — renders a separator line instead of an item,
 * }]
 */
export default {
  name: 'ActionsMenu',
  props: {
    items: { type: Array, default: () => [] },
    title: { type: String, default: 'Actions' }
  },

  data() {
    return {
      open: false,
      menuStyle: {}
    }
  },

  computed: {
    visibleItems() {
      const items = this.items.filter(it => it && it.show !== false)
      // Drop leading/trailing/double dividers left behind by hidden items
      return items.filter((it, i) => {
        if (!it.divider) return true
        const prev = items[i - 1]
        return i > 0 && i < items.length - 1 && !prev.divider
      })
    }
  },

  beforeUnmount() {
    this.unbind()
  },

  methods: {
    toggle() {
      this.open ? this.close() : this.show()
    },

    async show() {
      this.open = true
      this.menuStyle = { visibility: 'hidden' }
      await this.$nextTick()
      this.position()
      document.addEventListener('click', this.onClickOutside)
      document.addEventListener('keydown', this.onKeydown)
      window.addEventListener('scroll', this.close, true)
      window.addEventListener('resize', this.close)
    },

    close() {
      if (!this.open) return
      this.open = false
      this.unbind()
    },

    unbind() {
      document.removeEventListener('click', this.onClickOutside)
      document.removeEventListener('keydown', this.onKeydown)
      window.removeEventListener('scroll', this.close, true)
      window.removeEventListener('resize', this.close)
    },

    position() {
      const trigger = this.$refs.trigger?.getBoundingClientRect()
      const menu = this.$refs.menu
      if (!trigger || !menu) return
      const { offsetWidth: w, offsetHeight: h } = menu
      // Right-align with the trigger; flip above when there's no room below.
      const left = Math.max(8, trigger.right - w)
      const below = trigger.bottom + MENU_GAP
      const top = below + h > window.innerHeight - 8
        ? Math.max(8, trigger.top - h - MENU_GAP)
        : below
      this.menuStyle = { top: `${top}px`, left: `${left}px` }
    },

    select(item) {
      this.close()
      if (!item.to && typeof item.action === 'function') item.action()
    },

    onClickOutside(e) {
      if (this.$refs.menu?.contains(e.target) || this.$refs.trigger?.contains(e.target)) return
      this.close()
    },

    onKeydown(e) {
      if (e.key === 'Escape') this.close()
    }
  }
}
</script>
